import React, { Component, ErrorInfo, ReactNode } from 'react';

import { checkIsOnline } from '@/hooks/useNetworkStatus';

import { ErrorBoundary } from './ErrorBoundary';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Enhanced error boundary that also catches unhandled promise rejections
 * and async errors that occur outside of React's lifecycle
 */
export class AsyncErrorBoundary extends Component<Props, State> {
  private unhandledRejectionHandler:
    ((event: PromiseRejectionEvent) => void) | null = null;
  private errorHandler: ((event: ErrorEvent) => void) | null = null;

  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
    };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ error });

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Log to console in development
    if (import.meta.env.DEV) {
      console.error('AsyncErrorBoundary caught error:', error, errorInfo);
    }
  }

  /**
   * Check if an error is network-related (expected when offline)
   */
  private isNetworkError(reason: unknown): boolean {
    if (!reason) return false;

    const reasonObj = reason as { message?: string; name?: string };
    const message = reasonObj?.message || String(reason);
    const networkErrorPatterns = [
      'fetch',
      'network',
      'offline',
      'Failed to fetch',
      'NetworkError',
      'net::ERR_',
      'ECONNREFUSED',
      'ETIMEDOUT',
      'Load failed',
    ];

    return networkErrorPatterns.some(
      pattern =>
        message.toLowerCase().includes(pattern.toLowerCase()) ||
        reasonObj?.name === 'TypeError'
    );
  }

  public componentDidMount() {
    // Catch unhandled promise rejections
    this.unhandledRejectionHandler = (event: PromiseRejectionEvent) => {
      // When offline, ignore network-related errors - let the app handle offline gracefully
      // Uses enhanced checkIsOnline() which handles VPN scenarios
      const isOffline = !checkIsOnline();
      if (isOffline && this.isNetworkError(event.reason)) {
        event.preventDefault();
        return;
      }

      const error = new Error(
        event.reason?.message || event.reason || 'Unhandled Promise Rejection'
      );

      // Prevent default browser behavior
      event.preventDefault();

      // Update state to show error UI
      this.setState({
        hasError: true,
        error,
      });

      // Log in development
      if (import.meta.env.DEV) {
        console.error('Unhandled promise rejection:', event.reason);
      }
    };

    // Catch global errors
    this.errorHandler = (event: ErrorEvent) => {
      // When offline, ignore network-related errors
      // Uses enhanced checkIsOnline() which handles VPN scenarios
      const isOffline = !checkIsOnline();
      if (isOffline && this.isNetworkError(event.error || event.message)) {
        return;
      }

      const error = new Error(event.message || 'Unexpected error');

      // Only catch async errors (not already handled by React)
      if (event.error && !this.state.hasError) {
        this.setState({
          hasError: true,
          error,
        });
      }
    };

    // Add event listeners
    if (typeof window !== 'undefined') {
      window.addEventListener(
        'unhandledrejection',
        this.unhandledRejectionHandler
      );
      window.addEventListener('error', this.errorHandler);
    }
  }

  public componentWillUnmount() {
    // Clean up event listeners
    if (typeof window !== 'undefined') {
      if (this.unhandledRejectionHandler) {
        window.removeEventListener(
          'unhandledrejection',
          this.unhandledRejectionHandler
        );
      }
      if (this.errorHandler) {
        window.removeEventListener('error', this.errorHandler);
      }
    }
  }

  public render() {
    if (this.state.hasError) {
      // If custom fallback provided, use it
      if (this.props.fallback) {
        return <>{this.props.fallback}</>;
      }

      // Otherwise use the standard ErrorBoundary component
      // We trigger it by throwing the error
      throw this.state.error;
    }

    // Wrap children in standard ErrorBoundary for sync errors
    return (
      <ErrorBoundary fallback={this.props.fallback}>
        {this.props.children}
      </ErrorBoundary>
    );
  }
}
